import { format, isValid, parseISO, startOfDay, startOfMonth } from 'date-fns';
import type { CarbonAvoidedByStream } from '../types/esg-dashboard.type';
import type {
  CarbonAvoidanceStreamRow,
  CarbonCalculationRunStatus,
  EmissionFactorVersionUsed,
} from '../types/carbon-avoidance-report.type';
import { safeNumber } from './reportFormatters';

export const CARBON_REPORT_SESSION_KEYS = {
  startDate: 'carbonReportStartDate',
  endDate: 'carbonReportEndDate',
} as const;

export type CarbonReportPeriod = {
  startDate: Date;
  endDate: Date;
};

function defaultCarbonReportPeriod(): CarbonReportPeriod {
  const today = startOfDay(new Date());
  return {
    startDate: startOfMonth(today),
    endDate: today,
  };
}

export function readCarbonReportPeriodFromSession(): CarbonReportPeriod {
  const defaults = defaultCarbonReportPeriod();
  const startRaw = sessionStorage.getItem(CARBON_REPORT_SESSION_KEYS.startDate);
  const endRaw = sessionStorage.getItem(CARBON_REPORT_SESSION_KEYS.endDate);

  const startDate = startRaw ? parseISO(startRaw) : defaults.startDate;
  const endDate = endRaw ? parseISO(endRaw) : defaults.endDate;

  return {
    startDate: isValid(startDate) ? startOfDay(startDate) : defaults.startDate,
    endDate: isValid(endDate) ? startOfDay(endDate) : defaults.endDate,
  };
}

export function writeCarbonReportPeriodToSession(period: CarbonReportPeriod): void {
  sessionStorage.setItem(
    CARBON_REPORT_SESSION_KEYS.startDate,
    format(period.startDate, 'yyyy-MM-dd')
  );
  sessionStorage.setItem(
    CARBON_REPORT_SESSION_KEYS.endDate,
    format(period.endDate, 'yyyy-MM-dd')
  );
}

export function carbonReportPeriodToParams(period: CarbonReportPeriod): {
  start_date: string;
  end_date: string;
} {
  return {
    start_date: format(period.startDate, 'yyyy-MM-dd'),
    end_date: format(period.endDate, 'yyyy-MM-dd'),
  };
}

export function isCarbonReportPeriodValid(period: CarbonReportPeriod): boolean {
  return period.endDate >= period.startDate;
}

export function formatEfVersionsUsedForDisplay(
  versions: EmissionFactorVersionUsed[]
): string {
  if (!versions.length) return '—';
  return versions
    .map((ef) => `${ef.version_label} (${ef.run_count} run${ef.run_count === 1 ? '' : 's'})`)
    .join('; ');
}

export function mapReportStreamsToChartRows(
  rows: CarbonAvoidanceStreamRow[]
): CarbonAvoidedByStream[] {
  return rows.map((row) => ({
    material_label: row.material_label,
    total_avoided_tco2e: row.avoided_tco2e,
    emission_factor_versions: row.emission_factor_versions_used.map((ef) => ef.version_label),
  }));
}

/**
 * Display form of a material label. The backend sends the material's own name,
 * falling back to its category, then the literal 'unknown' — so these are free
 * text rather than a fixed set, and only the placeholder needs special casing.
 */
export function carbonMaterialLabel(label: string): string {
  if (!label || label === 'unknown') return 'Unknown';
  return label;
}

export function formatTco2e(value: unknown, decimals = 4): string {
  const n = safeNumber(value);
  if (n === null) return '0';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

export function formatEfVersions(versions: string[]): string {
  if (!versions.length) return '';
  return versions.map((v) => (v.startsWith('v') ? `EF ${v}` : `EF ${v}`)).join(', ');
}

export function formatTonnes(value: unknown, decimals = 2): string {
  const n = safeNumber(value);
  if (n === null) return '0';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

export function formatReportPeriodLabel(startDate: string, endDate: string): string {
  const start = parseISO(startDate);
  const end = parseISO(endDate);
  if (!isValid(start) || !isValid(end)) return `${startDate} – ${endDate}`;
  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameYear && start.getMonth() === end.getMonth() && start.getDate() === end.getDate()) {
    return format(start, 'MMM d, yyyy');
  }
  if (sameYear) {
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`;
  }
  return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
}

export const CALCULATION_WARNING_LABELS: Record<string, string> = {
  UNSUPPORTED_MATERIAL: 'Material is not cardboard or organic',
  WEIGHT_UNIT_AMBIGUOUS: 'Amount unit is not a mass unit; treated as kg with warning',
  ZERO_WEIGHT: 'Resolved weight is zero or negative',
  NO_ACTIVE_FACTOR: 'No emission factor for stream and completion date',
  DELIVERY_NOT_COMPLETED: 'Order or delivery is not in completed state',
};

export function formatRunStatus(status: CarbonCalculationRunStatus): string {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'skipped':
      return 'Skipped';
    case 'failed':
      return 'Failed';
    case 'pending':
      return 'Pending';
    default:
      return status;
  }
}

export function truncateRunId(runId: string, visible = 8): string {
  if (runId.length <= visible + 3) return runId;
  return `${runId.slice(0, visible)}…`;
}

/** Read total avoided tCO2e from dashboard payload; falls back to legacy CO2 keys. */
export function pickTotalAvoidedTco2e(payload: unknown): number {
  if (payload == null || typeof payload !== 'object') return 0;
  const o = payload as Record<string, unknown>;

  const primary = safeNumber(o.total_avoided_tco2e);
  if (primary !== null) return primary;

  const legacyCandidates: Array<{ key: string; factor: number }> = [
    { key: 'total_co2e_tons', factor: 1 },
    { key: 'total_co2e_kg', factor: 1 / 1000 },
    { key: 'total_co2_tons', factor: 1 },
    { key: 'total_co2_kg', factor: 1 / 1000 },
  ];

  for (const c of legacyCandidates) {
    const v = safeNumber(o[c.key]);
    if (v !== null) return v * c.factor;
  }

  return 0;
}
